import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../../../config";
import RecentAnnouncementsWidget from "../../announcements/RecentAnnouncementsWidget";
import s from "./RegistrarDashboard.module.css";

const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}
function studentDisplayName(st) {
  if (!st) return "Unknown student";
  const lf = [st.lastName, st.firstName].filter(Boolean).join(", ");
  return lf || st.studentNumber || "Unnamed student";
}

/** Status taxonomy the registrar cares about. Mirrors the values
 *  StudentProfileForm / EnrollmentsPanel write back to /api/students.
 *  Order here drives the pipeline column order. */
const STATUS_ORDER = ["PENDING", "ENROLLED", "DROPPED", "GRADUATED"];
const STATUS_META = {
  PENDING:   { label: "Pending",   tone: "warn",   blurb: "Awaiting section assignment" },
  ENROLLED:  { label: "Enrolled",  tone: "ok",     blurb: "Active in school" },
  DROPPED:   { label: "Dropped",   tone: "danger", blurb: "Left before completion" },
  GRADUATED: { label: "Graduated", tone: "info",   blurb: "Completed program" },
};

/**
 * Registrar-specific dashboard. Surfaces the daily-work signals a registrar
 * actually scans — pending admissions, sections still missing an adviser,
 * sections with zero enrolled, and the recent enrollment activity feed.
 *
 * Read-only: no mutations, no permission elevation. Every fetch falls back
 * to [] on failure so a single backend hiccup never blocks the dashboard.
 */
export default function RegistrarDashboard({ user }) {
  const [schoolYears, setSchoolYears] = useState([]);
  const [semesters,   setSemesters]   = useState([]);
  const [sections,    setSections]    = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [students,    setStudents]    = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [employees,   setEmployees]   = useState([]);
  const [loading,     setLoading]     = useState(true);

  const [syId,  setSyId]  = useState("");
  const [semId, setSemId] = useState("");

  // Drill-down modal target — set by clicking a stat tile or a pipeline
  // column. `null` means "no modal open". Kept as a plain string so we
  // can't accidentally leak a stale object across fetches.
  const [drilldown, setDrilldown] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/api/school-years`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/semesters`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/sections`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/grade-levels`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/students`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/enrollments`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/employees?all=true`).then(r => r.ok ? r.json() : []),
    ]).then(([sy, sem, sec, gl, stu, enr, emp]) => {
      if (cancelled) return;
      setSchoolYears(Array.isArray(sy) ? sy : []);
      setSemesters(Array.isArray(sem) ? sem : []);
      setSections(Array.isArray(sec) ? sec : []);
      setGradeLevels(Array.isArray(gl) ? gl : []);
      setStudents(Array.isArray(stu) ? stu : []);
      setEnrollments(Array.isArray(enr) ? enr : []);
      setEmployees(Array.isArray(emp) ? emp : []);
      const activeSy  = (sy || []).find(y => y.active) || (sy || [])[0];
      if (activeSy) setSyId(String(activeSy.id));
      const activeSem = (sem || []).find(x => x.active)
        || (sem || []).find(x => !activeSy || String(x.schoolYearId) === String(activeSy.id))
        || (sem || [])[0];
      if (activeSem) setSemId(String(activeSem.id));
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Reset Semester if it no longer belongs to the chosen SY.
  useEffect(() => {
    if (!syId) return;
    const stillValid = semesters.some(x => String(x.id) === semId && String(x.schoolYearId) === String(syId));
    if (stillValid) return;
    const inSy = semesters.filter(x => String(x.schoolYearId) === String(syId));
    const fresh = inSy.find(x => x.active) || inSy[0];
    setSemId(fresh ? String(fresh.id) : "");
  }, [syId, semId, semesters]);

  // ESC closes any open drilldown. Listener mounted only while the modal
  // is open — no global handler when idle.
  useEffect(() => {
    if (!drilldown) return;
    const onKey = (e) => { if (e.key === "Escape") setDrilldown(null); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [drilldown]);

  // Fast lookups.
  const gradeLevelById = useMemo(() => Object.fromEntries(gradeLevels.map(g => [g.id, g])), [gradeLevels]);
  const sectionById    = useMemo(() => Object.fromEntries(sections.map(x => [x.id, x])), [sections]);
  const studentById    = useMemo(() => Object.fromEntries(students.map(x => [x.id, x])), [students]);
  const employeeById   = useMemo(() => Object.fromEntries(employees.map(x => [x.id, x])), [employees]);

  const activeSy  = useMemo(() => schoolYears.find(y => String(y.id) === syId) || null, [schoolYears, syId]);
  const activeSem = useMemo(() => semesters.find(x => String(x.id) === semId) || null, [semesters, semId]);

  // Sections scoped to the selected SY. Empty SY filter ⇒ all sections.
  const sectionsInSY = useMemo(
    () => sections.filter(sec => !syId || String(sec.schoolYearId) === String(syId)),
    [sections, syId]
  );

  // Enrollments scoped to the selected SY + Sem.
  const scopedEnrollments = useMemo(() => {
    return enrollments.filter(e => {
      if (syId  && String(e.schoolYearId) !== String(syId))  return false;
      if (semId && String(e.semesterId)   !== String(semId)) return false;
      return true;
    });
  }, [enrollments, syId, semId]);

  // ── KPIs ──────────────────────────────────────────────────────────────
  const monthStart = useMemo(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d.getTime();
  }, []);

  // Group students by status. Empty / unknown status falls into PENDING
  // so the registrar can still find them and process them.
  const studentsByStatus = useMemo(() => {
    const map = { PENDING: [], ENROLLED: [], DROPPED: [], GRADUATED: [] };
    for (const st of students) {
      const k = (st.status || "PENDING").toUpperCase();
      if (map[k]) map[k].push(st);
      else map.PENDING.push(st);
    }
    return map;
  }, [students]);

  const enrolledCount = studentsByStatus.ENROLLED.length;
  const pendingCount  = studentsByStatus.PENDING.length;

  const newThisMonth = useMemo(() => {
    let n = 0;
    for (const e of scopedEnrollments) {
      const t = e.enrolledAt ? new Date(e.enrolledAt).getTime() : 0;
      if (t >= monthStart) n += 1;
    }
    return n;
  }, [scopedEnrollments, monthStart]);

  // Sections lacking an adviser, scoped to the active SY.
  const sectionsWithoutAdviser = useMemo(
    () => sectionsInSY.filter(sec => sec.adviserStaffId == null),
    [sectionsInSY]
  );

  // Section → enrolled student count map, scoped to selected SY+Sem.
  // Uses scopedEnrollments so the "empty" flag reflects the current term.
  const enrolledStudentsBySection = useMemo(() => {
    const map = new Map();
    for (const e of scopedEnrollments) {
      const status = (e.status || "ENROLLED").toUpperCase();
      if (status !== "ENROLLED" || e.sectionId == null) continue;
      if (!map.has(e.sectionId)) map.set(e.sectionId, new Set());
      map.get(e.sectionId).add(e.studentId);
    }
    return map;
  }, [scopedEnrollments]);

  const emptySections = useMemo(
    () => sectionsInSY.filter(sec => !enrolledStudentsBySection.has(sec.id)),
    [sectionsInSY, enrolledStudentsBySection]
  );

  // Recent enrollment activity — last 10, newest first, scoped to SY+Sem.
  const recentEnrollments = useMemo(() => {
    return [...scopedEnrollments]
      .filter(e => e.enrolledAt)
      .sort((a, b) => new Date(b.enrolledAt).getTime() - new Date(a.enrolledAt).getTime())
      .slice(0, 10);
  }, [scopedEnrollments]);

  // Sections sorted for the Roster Health panel — issues bubble up first
  // so the registrar's eye lands on actionable rows.
  const sectionsForHealth = useMemo(() => {
    const score = (sec) => {
      const noAdviser = sec.adviserStaffId == null;
      const empty     = !enrolledStudentsBySection.has(sec.id);
      // Lower score = bubble up. NoAdviser + Empty is worst (0), single-issue (1), healthy (2).
      if (noAdviser && empty) return 0;
      if (noAdviser || empty) return 1;
      return 2;
    };
    return [...sectionsInSY].sort((a, b) => {
      const sa = score(a), sb = score(b);
      if (sa !== sb) return sa - sb;
      const ga = gradeLevelById[a.gradeLevelId];
      const gb = gradeLevelById[b.gradeLevelId];
      const oa = ga?.ordering ?? 999;
      const ob = gb?.ordering ?? 999;
      if (oa !== ob) return oa - ob;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [sectionsInSY, enrolledStudentsBySection, gradeLevelById]);

  const monthLabel = new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const displayName = user?.linkedEntity?.displayName || user?.username || "";

  return (
    <div className={s.wrap}>
      {/* Hero */}
      <header className={s.header}>
        <div className={s.eyebrow}>REGISTRAR · DASHBOARD</div>
        <h1 className={s.title}>
          Welcome{displayName ? `, ${displayName}` : ""}.
        </h1>
        <p className={s.sub}>
          Daily registrar view — pending admissions, section assignments, adviser coverage,
          and the live enrollment feed. Pick a school year and term to focus the snapshot.
        </p>
        <div className={s.heroMeta}>
          <span className={s.heroMetaPill}>Role: {user?.role || "Registrar"}</span>
          {user?.linkedEntity?.displayName && (
            <span className={s.heroMetaPill}>Linked: {user.linkedEntity.displayName}</span>
          )}
          <span className={s.heroMetaSpacer} />
          <span className={s.heroMetaSoft}>{monthLabel}</span>
        </div>
      </header>

      {/* Filter */}
      <div className={s.filterCard}>
        <label className={s.filterField}>
          <span>School Year</span>
          <select value={syId} onChange={e => setSyId(e.target.value)}>
            {schoolYears.map(y => (
              <option key={y.id} value={y.id}>
                {y.label}{y.active ? " · Active" : ""}
              </option>
            ))}
          </select>
        </label>
        <label className={s.filterField}>
          <span>Semester</span>
          <select value={semId} onChange={e => setSemId(e.target.value)}>
            {semesters
              .filter(x => !syId || String(x.schoolYearId) === String(syId))
              .map(x => (
                <option key={x.id} value={x.id}>
                  {x.label}{x.active ? " · Active" : ""}
                </option>
              ))}
          </select>
        </label>
        <div className={s.filterActive}>
          {activeSy  && <span className={s.activePill}>{activeSy.label}</span>}
          {activeSem && <span className={s.activePill}>{activeSem.label}</span>}
        </div>
      </div>

      {/* Stat tiles */}
      <div className={s.statGrid}>
        <StatTile
          index={0} label="Enrolled Students"
          value={loading ? "…" : enrolledCount}
          sub={loading ? "" : `${students.length} total on file`}
          tone="ok"
          onClick={() => setDrilldown("enrolled")} />
        <StatTile
          index={1} label="Pending Admissions"
          value={loading ? "…" : pendingCount}
          sub={loading ? "" : "Awaiting section assignment"}
          tone={pendingCount > 0 ? "warn" : "ok"}
          onClick={() => setDrilldown("pending")} />
        <StatTile
          index={2} label="New This Month"
          value={loading ? "…" : newThisMonth}
          sub={loading ? "" : monthLabel}
          onClick={() => setDrilldown("recent")} />
        <StatTile
          index={3} label="Sections"
          value={loading ? "…" : sectionsInSY.length}
          sub={loading ? "" : `for ${activeSy?.label || "—"}`}
          onClick={() => setDrilldown("sections")} />
        <StatTile
          index={4} label="Without Adviser"
          value={loading ? "…" : sectionsWithoutAdviser.length}
          sub={loading ? "" : "Sections missing an adviser"}
          tone={sectionsWithoutAdviser.length > 0 ? "warn" : "ok"}
          onClick={() => setDrilldown("noAdviser")} />
        <StatTile
          index={5} label="Empty Sections"
          value={loading ? "…" : emptySections.length}
          sub={loading ? "" : "No enrolled students this term"}
          tone={emptySections.length > 0 ? "warn" : "ok"}
          onClick={() => setDrilldown("empty")} />
      </div>

      {/* Admissions Pipeline + Section Roster Health */}
      <div className={s.panelGrid}>
        <section className={`${s.panel} ${s.panelWide}`}>
          <header className={s.panelHead}>
            <div>
              <h2 className={s.panelTitle}>Admissions Pipeline</h2>
              <p className={s.panelSub}>
                Where every student stands in the registrar's flow. Click a column to drill in.
              </p>
            </div>
            <a className={s.panelAction} href="/admissions">+ New admission →</a>
          </header>

          {loading ? (
            <div className={s.empty}>Loading…</div>
          ) : (
            <div className={s.pipelineGrid}>
              {STATUS_ORDER.map((key, idx) => {
                const meta = STATUS_META[key];
                const list = studentsByStatus[key];
                return (
                  <button
                    type="button"
                    key={key}
                    style={{ "--i": idx }}
                    className={`${s.pipelineCol} ${s[`pipelineCol_${meta.tone}`]}`}
                    onClick={() => setDrilldown(`status:${key}`)}
                    aria-label={`Show ${list.length} ${meta.label.toLowerCase()} student${list.length === 1 ? "" : "s"}`}>
                    <div className={s.pipelineHead}>
                      <span className={s.pipelineLabel}>{meta.label}</span>
                      <span className={`${s.pipelineCount} ${s[`pipelineCount_${meta.tone}`]}`}>
                        {list.length}
                      </span>
                    </div>
                    <div className={s.pipelineBlurb}>{meta.blurb}</div>
                    <div className={s.pipelinePeek}>
                      {list.length === 0 ? (
                        <span className={s.pipelineEmpty}>None</span>
                      ) : (
                        list.slice(0, 4).map(st => (
                          <div key={st.id} className={s.pipelinePeekRow}>
                            <div
                              className={s.pipelinePeekAvatar}
                              style={{ background: avatarColor(studentDisplayName(st)) }}
                              aria-hidden="true">
                              {initials(studentDisplayName(st))}
                            </div>
                            <span className={s.pipelinePeekName}>{studentDisplayName(st)}</span>
                          </div>
                        ))
                      )}
                      {list.length > 4 && (
                        <div className={s.pipelinePeekMore}>+{list.length - 4} more</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className={s.panel}>
          <header className={s.panelHead}>
            <div>
              <h2 className={s.panelTitle}>Section Roster Health</h2>
              <p className={s.panelSub}>
                Issues bubble up first. Click any section to inspect it.
              </p>
            </div>
            <a className={s.panelAction} href="/academic-directory">Directory →</a>
          </header>

          {loading ? (
            <div className={s.empty}>Loading…</div>
          ) : sectionsForHealth.length === 0 ? (
            <div className={s.empty}>No sections for the selected school year.</div>
          ) : (
            <ul className={s.healthList}>
              {sectionsForHealth.slice(0, 12).map((sec, i) => {
                const gl = gradeLevelById[sec.gradeLevelId];
                const adviser = sec.adviserStaffId != null ? employeeById[sec.adviserStaffId] : null;
                const studentSet = enrolledStudentsBySection.get(sec.id);
                const count = studentSet ? studentSet.size : 0;
                const noAdviser = !adviser;
                const empty = count === 0;
                const tone = (noAdviser && empty) ? "danger" : (noAdviser || empty) ? "warn" : "ok";
                return (
                  <li
                    key={sec.id}
                    style={{ "--i": i }}
                    className={`${s.healthRow} ${s[`healthRow_${tone}`]}`}>
                    <div className={s.healthAvatar} style={{ background: avatarColor(sec.name) }}>
                      {initials(sec.name)}
                    </div>
                    <div className={s.healthBody}>
                      <div className={s.healthName}>
                        {sec.name}
                        {gl && <span className={s.healthGrade}>· {gl.name}</span>}
                      </div>
                      <div className={s.healthMeta}>
                        {adviser
                          ? <span>Adviser: <b>{adviser.name}</b></span>
                          : <span className={s.badgeWarn}>No adviser</span>}
                        {empty
                          ? <span className={s.badgeWarn}>Empty</span>
                          : <span className={s.badgeOk}>{count} enrolled</span>}
                      </div>
                    </div>
                  </li>
                );
              })}
              {sectionsForHealth.length > 12 && (
                <li className={s.healthMore}>
                  +{sectionsForHealth.length - 12} more · open the directory to see all
                </li>
              )}
            </ul>
          )}
        </section>
      </div>

      {/* Recent Activity */}
      <section className={s.activitySection}>
        <header className={s.panelHead}>
          <div>
            <h2 className={s.panelTitle}>Recent Activity</h2>
            <p className={s.panelSub}>
              Last 10 enrollment events
              {activeSy ? ` for ${activeSy.label}` : ""}
              {activeSem ? ` · ${activeSem.label}` : ""}.
            </p>
          </div>
        </header>
        {loading ? (
          <div className={s.empty}>Loading…</div>
        ) : recentEnrollments.length === 0 ? (
          <div className={s.empty}>No recent enrollment activity for this scope.</div>
        ) : (
          <ul className={s.activityList}>
            {recentEnrollments.map((e, i) => {
              const st  = studentById[e.studentId];
              const sec = e.sectionId ? sectionById[e.sectionId] : null;
              const gl  = sec ? gradeLevelById[sec.gradeLevelId] : null;
              const when = new Date(e.enrolledAt).toLocaleString(undefined, {
                month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
              });
              const stName = studentDisplayName(st) || `Student #${e.studentId}`;
              const status = (e.status || "ENROLLED").toUpperCase();
              const meta = STATUS_META[status] || STATUS_META.ENROLLED;
              return (
                <li key={e.id} style={{ "--i": i }} className={s.activityRow}>
                  <div className={s.activityAvatar} style={{ background: avatarColor(stName) }}>
                    {initials(stName)}
                  </div>
                  <div className={s.activityBody}>
                    <div className={s.activityTitle}>
                      <span className={s.activityName}>{stName}</span>
                      <span className={`${s.activityStatus} ${s[`activityStatus_${meta.tone}`]}`}>
                        {meta.label}
                      </span>
                    </div>
                    <div className={s.activityMeta}>
                      {sec ? (gl ? `${gl.name} · ${sec.name}` : sec.name) : "No section"}
                      <span className={s.activitySep}>·</span>
                      <span>{when}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <RecentAnnouncementsWidget />

      {drilldown && createPortal(
        <DrilldownModal
          target={drilldown}
          onClose={() => setDrilldown(null)}
          ctx={{
            students, studentsByStatus, sectionsInSY,
            sectionsWithoutAdviser, emptySections,
            recentEnrollments, sectionById, gradeLevelById, employeeById,
            studentById, enrolledStudentsBySection,
            activeSy, activeSem,
          }}
        />,
        document.body
      )}
    </div>
  );
}

/** Single stat tile — clickable button form so the whole card is one
 *  hit-target. `index` drives the entrance-animation stagger. */
function StatTile({ index, label, value, sub, tone, onClick }) {
  const toneClass = tone === "warn" ? s.statWarn : tone === "ok" ? s.statOk : "";
  return (
    <button
      type="button"
      style={{ "--i": index }}
      className={`${s.stat} ${toneClass}`}
      onClick={onClick}>
      <div className={s.statLabel}>{label}</div>
      <div className={s.statValue}>{value}</div>
      {sub && <div className={s.statSub}>{sub}</div>}
      <div className={s.statHint}>View details →</div>
    </button>
  );
}

/** Portaled drill-down modal. Resolves its title + body from the `target`
 *  string so the parent only stores a tiny key, never a stale object. */
function DrilldownModal({ target, onClose, ctx }) {
  const titles = {
    enrolled:  { title: "Enrolled Students",   sub: `${ctx.studentsByStatus.ENROLLED.length} active in school` },
    pending:   { title: "Pending Admissions",  sub: `${ctx.studentsByStatus.PENDING.length} awaiting enrollment` },
    recent:    { title: "Recent Enrollments",  sub: `Last 10 events for ${ctx.activeSy?.label || "—"} · ${ctx.activeSem?.label || "—"}` },
    sections:  { title: "Sections",            sub: `${ctx.sectionsInSY.length} for ${ctx.activeSy?.label || "—"}` },
    noAdviser: { title: "Sections Without Adviser", sub: `${ctx.sectionsWithoutAdviser.length} need an adviser assigned` },
    empty:     { title: "Empty Sections",      sub: `${ctx.emptySections.length} with no enrolled students this term` },
  };
  let title, sub, body;
  if (target.startsWith("status:")) {
    const key = target.slice("status:".length);
    const meta = STATUS_META[key];
    const list = ctx.studentsByStatus[key] || [];
    title = `${meta?.label || key} Students`;
    sub = `${list.length} total · ${meta?.blurb || ""}`;
    body = <StudentList list={list} ctx={ctx} />;
  } else {
    const t = titles[target] || { title: target, sub: "" };
    title = t.title; sub = t.sub;
    if (target === "enrolled")  body = <StudentList list={ctx.studentsByStatus.ENROLLED}  ctx={ctx} />;
    if (target === "pending")   body = <StudentList list={ctx.studentsByStatus.PENDING}   ctx={ctx} />;
    if (target === "recent")    body = <RecentList  list={ctx.recentEnrollments} ctx={ctx} />;
    if (target === "sections")  body = <SectionList list={ctx.sectionsInSY} ctx={ctx} />;
    if (target === "noAdviser") body = <SectionList list={ctx.sectionsWithoutAdviser} ctx={ctx} />;
    if (target === "empty")     body = <SectionList list={ctx.emptySections} ctx={ctx} />;
  }

  return (
    <div
      className={s.modalBackdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="reg-drill-title">
      <div className={s.modalCard} onClick={e => e.stopPropagation()}>
        <header className={s.modalHead}>
          <div>
            <h3 id="reg-drill-title" className={s.modalTitle}>{title}</h3>
            {sub && <p className={s.modalSub}>{sub}</p>}
          </div>
          <button
            type="button"
            className={s.modalClose}
            onClick={onClose}
            aria-label="Close details">
            ✕
          </button>
        </header>
        <div className={s.modalBody}>{body}</div>
        <footer className={s.modalFoot}>
          <button type="button" className={s.modalCloseBtn} onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>
  );
}

function StudentList({ list, ctx }) {
  if (!list || list.length === 0) {
    return <div className={s.empty}>No students in this group.</div>;
  }
  return (
    <ul className={s.modalList}>
      {list.map((st, i) => {
        const sec = st.currentSectionId ? ctx.sectionById[st.currentSectionId] : null;
        const gl  = sec ? ctx.gradeLevelById[sec.gradeLevelId] : null;
        const name = studentDisplayName(st);
        const status = (st.status || "PENDING").toUpperCase();
        const meta = STATUS_META[status] || STATUS_META.PENDING;
        return (
          <li key={st.id} style={{ "--i": Math.min(i, 24) }} className={s.modalRow}>
            <div className={s.modalAvatar} style={{ background: avatarColor(name) }}>
              {initials(name)}
            </div>
            <div className={s.modalRowBody}>
              <div className={s.modalRowName}>{name}</div>
              <div className={s.modalRowMeta}>
                {st.studentNumber && <span>#{st.studentNumber}</span>}
                {sec
                  ? <span>{gl ? `${gl.name} · ${sec.name}` : sec.name}</span>
                  : <span className={s.badgeWarn}>No section</span>}
                <span className={`${s.modalStatusPill} ${s[`modalStatusPill_${meta.tone}`]}`}>
                  {meta.label}
                </span>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function SectionList({ list, ctx }) {
  if (!list || list.length === 0) {
    return <div className={s.empty}>Nothing matches — every section is healthy for this scope.</div>;
  }
  return (
    <ul className={s.modalList}>
      {list.map((sec, i) => {
        const gl = ctx.gradeLevelById[sec.gradeLevelId];
        const adviser = sec.adviserStaffId != null ? ctx.employeeById[sec.adviserStaffId] : null;
        const studentSet = ctx.enrolledStudentsBySection.get(sec.id);
        const count = studentSet ? studentSet.size : 0;
        return (
          <li key={sec.id} style={{ "--i": Math.min(i, 24) }} className={s.modalRow}>
            <div className={s.modalAvatar} style={{ background: avatarColor(sec.name) }}>
              {initials(sec.name)}
            </div>
            <div className={s.modalRowBody}>
              <div className={s.modalRowName}>
                {sec.name}
                {gl && <span className={s.healthGrade}>· {gl.name}</span>}
              </div>
              <div className={s.modalRowMeta}>
                {adviser
                  ? <span>Adviser: <b>{adviser.name}</b></span>
                  : <span className={s.badgeWarn}>No adviser</span>}
                {count > 0
                  ? <span className={s.badgeOk}>{count} enrolled</span>
                  : <span className={s.badgeWarn}>Empty</span>}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function RecentList({ list, ctx }) {
  if (!list || list.length === 0) {
    return <div className={s.empty}>No recent activity for this scope.</div>;
  }
  return (
    <ul className={s.modalList}>
      {list.map((e, i) => {
        const st  = ctx.studentById[e.studentId];
        const sec = e.sectionId ? ctx.sectionById[e.sectionId] : null;
        const gl  = sec ? ctx.gradeLevelById[sec.gradeLevelId] : null;
        const name = studentDisplayName(st) || `Student #${e.studentId}`;
        const when = e.enrolledAt
          ? new Date(e.enrolledAt).toLocaleString(undefined, {
              month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
            })
          : "—";
        const status = (e.status || "ENROLLED").toUpperCase();
        const meta = STATUS_META[status] || STATUS_META.ENROLLED;
        return (
          <li key={e.id} style={{ "--i": Math.min(i, 24) }} className={s.modalRow}>
            <div className={s.modalAvatar} style={{ background: avatarColor(name) }}>
              {initials(name)}
            </div>
            <div className={s.modalRowBody}>
              <div className={s.modalRowName}>{name}</div>
              <div className={s.modalRowMeta}>
                {sec
                  ? <span>{gl ? `${gl.name} · ${sec.name}` : sec.name}</span>
                  : <span className={s.badgeWarn}>No section</span>}
                <span className={`${s.modalStatusPill} ${s[`modalStatusPill_${meta.tone}`]}`}>
                  {meta.label}
                </span>
                <span className={s.activitySep}>·</span>
                <span>{when}</span>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
